import { useEffect, useState } from 'react';

import { format } from 'date-fns';
import ReactHtmlParser from 'react-html-parser';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Box,
  Button,
  Chip,
  styled as muiStyled,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import StatusChip from '../../../components/chips/StatusChip';
import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { useDeactivateReportedComment } from '../../../lib/comment/hooks';
import { useDeleteComment } from '../../../lib/comment/hooks/useDeleteComment';
import useGetAllReportedComments from '../../../lib/comment/hooks/useGetAllReportedComments';
import { displayUserName, SortDirection } from '../../../lib/util/helpers';

const ReportedCommentsList = () => {
  const navigate = useNavigate();
  const [optionsUpdated, setOptionsUpdated] = useState(true);
  const handleEditReportedComment = (id: number) => navigate(`/cms/reported-comments/${id}`);
  const { mutateAsync: deleteComment, isLoading: isLoadingDeleteComment } = useDeleteComment();
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const { mutateAsync: deactivateComment } = useDeactivateReportedComment();

  const {
    data: reportedComments,
    isLoading,
    error,
  } = useGetAllReportedComments({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  useEffect(() => {
    if (!optionsUpdated) {
      return;
    }
    setOptionsUpdated(false);
  }, [optionsUpdated, page, sort, search]);

  useEffect(() => {
    setOptionsUpdated(true);
  }, [setOptionsUpdated, page, sort, search]);

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Date',
      name: 'comment.lastReportedDate',
    },
    {
      text: 'Comment',
      name: 'comment.message',
    },
    {
      text: '# of Reports',
      name: 'comment_report_count',
    },
    {
      text: `Author`,
      name: 'author.username',
    },
    {
      text: 'Article Post Title',
      name: 'post.title',
    },
    {
      text: 'Reported by Admin',
      name: 'comment.reportedByAdmin',
    },
    {
      text: 'Status',
      name: 'comment.deactivated',
    },
    {
      text: 'Actions',
      name: 'actions',
    },
  ];

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5" sx={{ marginRight: '20px' }}>
          Reported Comments
        </Typography>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
      </FullWidthBar>

      <TableContainer sx={{ backgroundColor: 'white' }}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              reportedComments.data &&
              reportedComments.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index}>
                    <TableCell>{format(new Date(row.lastReportedDate), 'MM/dd/yyyy hh:mm aaa')}</TableCell>
                    <TableCell>{ReactHtmlParser(row.message)}</TableCell>
                    <TableCell>{row.reportCount}</TableCell>
                    <TableCell>{displayUserName(row.post.author)}</TableCell>
                    <TableCell>{row.post.title}</TableCell>
                    <TableCell>{row.isReportedByAdmin ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{row.deactivated ? <StatusChip deactivated /> : <Chip label="Reported" color="warning" />}</TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditReportedComment(row.id)} type="button" style={{ textTransform: 'capitalize' }}>
                        Edit
                      </Button>

                      <ConfirmDialog
                        trigger={(open) => (
                          <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                            Delete
                          </Button>
                        )}
                        title="Delete Comment"
                        description="Are you sure you want to delete this comment?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await deleteComment(row.id);
                          close();
                        }}
                        disabled={isLoadingDeleteComment}
                        loading={isLoadingDeleteComment}
                      />

                      <ConfirmDialog
                        trigger={(open) => (
                          <Button
                            onClick={open}
                            sx={{ textTransform: 'capitalize' }}
                            type="button"
                            disabled={row.deactivated ? true : false}
                          >
                            {row.deactivated ? 'Deactivated' : 'Deactivate'}
                          </Button>
                        )}
                        title="Deactivate Reported Post Comment"
                        description="Are you sure you want to deactivate this reported post comment?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await deactivateComment({ reportedCommentId: row.id });
                          close();
                        }}
                      />
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {reportedComments.data.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                  No reported comments records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {reportedComments.total !== null && reportedComments.total !== 0 && (
        <FullWidthBar>
          <Typography variant="subtitle1">Total: {reportedComments.total}</Typography>
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(reportedComments.total / Defaults.LIST_PAGE_SIZE)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        </FullWidthBar>
      )}
    </Container>
  );
};

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = muiStyled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

export default ReportedCommentsList;
